import { isPlatformBrowser } from '@angular/common';
import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import { UrlTree } from '@angular/router';
import { BaseSiteService, PageType } from '@spartacus/core';
import { LogoutGuard } from '@spartacus/storefront';
import { from, NEVER, Observable } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';

import { LoginEntryService } from '../../../../app/shared/services/login-entry.service';
import {
  getTenantAndLangFromPath,
  navigateToLoginRedirectGate,
} from '../../../../app/utils/build-login-url';

/**
 * Sends users straight to the new login gate after logout instead of the
 * stock `home` cxRoute, avoiding a homepage flash. Only when the new login
 * page flag is on and the user is not SSO; otherwise stock behaviour.
 */
@Injectable({ providedIn: 'root' })
export class VscaLogoutGuard extends LogoutGuard {
  private readonly loginEntryService = inject(LoginEntryService);
  private readonly baseSiteService = inject(BaseSiteService);
  private readonly platformId = inject(PLATFORM_ID);

  override canActivate(): Observable<boolean | UrlTree> {
    if (!isPlatformBrowser(this.platformId)) {
      return from(this.logout()).pipe(switchMap(() => this.stockRedirect()));
    }

    const isSso = this.isSsoUser();
    return from(this.logout()).pipe(
      switchMap(() => {
        if (isSso) {
          return this.stockRedirect();
        }

        return this.loginEntryService.whenNewLoginPageEnabled().pipe(
          switchMap((enabled) =>
            enabled ? this.redirectToLoginGate() : this.stockRedirect()
          )
        );
      })
    );
  }

  private redirectToLoginGate(): Observable<boolean | UrlTree> {
    const ctx = getTenantAndLangFromPath();
    if (ctx) {
      navigateToLoginRedirectGate(ctx.tenant, ctx.lang);
      return NEVER;
    }

    // No tenant/lang in the URL: fall back to the active base site, and if
    // even that is missing, don't strand the user — use stock behaviour.
    return this.baseSiteService.getActive().pipe(
      take(1),
      switchMap((baseSiteId) => {
        if (baseSiteId) {
          navigateToLoginRedirectGate(baseSiteId, 'en');
          return NEVER;
        }
        return this.stockRedirect();
      })
    );
  }

  /** Stock LogoutGuard redirect: CMS logout page if present, else `getRedirectUrl()`. */
  private stockRedirect(): Observable<boolean | UrlTree> {
    return this.cms
      .hasPage({
        id: this.semanticPathService.get('logout') ?? '',
        type: PageType.CONTENT_PAGE,
      })
      .pipe(map((hasPage) => (hasPage ? hasPage : this.getRedirectUrl())));
  }

  private isSsoUser(): boolean {
    return localStorage.hasOwnProperty('ssoEnabled');
  }
}
